import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import {
  CreditCard,
  Bell,
  Languages,
  User,
  Loader2,
  ArrowLeft,
  ChevronRight,
  MessageCircle,
	Gift,
} from "lucide-react";
import {
  BillingTab,
  NotificationsTab,
  LanguageTab,
  ProfileTab,
	ReferralsTab,
  type TabType,
} from "../components/settings";
import { confirmPayment } from "../services/BillingService";
import { toast } from "react-toastify";
import { track } from "../utils/analytics";
import { sanitizeAuthSource } from "../utils/authReturn";
import { isFreeTier } from "../utils/userUtils";
import FreeAccountRows from "../components/settings/FreeAccountRows";

// LOCAL DEVELOPMENT OVERRIDE. Production commit b8b68829 keeps this false; do not push true.
const MEMBER_REFERRALS_ENABLED = true;

export default function SettingsPage() {
  const { user, load } = useUserContext();
  const { t, currentLanguage, setLanguage } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const hasProcessedPayment = useRef(false);

  // Handle tab query parameter
  useEffect(() => {
    const tabParam = searchParams.get("tab");
		const allowedTabs = ["profile", "billing", "notifications", "language"];
		if (MEMBER_REFERRALS_ENABLED) allowedTabs.push("referrals");
		if (tabParam && allowedTabs.includes(tabParam)) {
			setActiveTab(tabParam as TabType);
			if (tabParam === "billing") {
				track("paywall_viewed", {
					source: sanitizeAuthSource(searchParams.get("upgradeSource")) || "settings",
				});
			}
			// Remove tab param from URL after setting it
			searchParams.delete("tab");
			searchParams.delete("upgradeSource");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle return from Stripe redirect after payment confirmation
  useEffect(() => {
    const paymentIntentId = searchParams.get("payment_intent");

    // Only process once per payment intent
    if (paymentIntentId && !hasProcessedPayment.current) {
      hasProcessedPayment.current = true;
      handleStripeReturn(paymentIntentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleStripeReturn = async (paymentIntentId: string) => {
    setIsProcessingPayment(true);
    setActiveTab("billing"); // Switch to billing tab to show the process

    try {
      // Call the backend to confirm the payment and create the subscription
      await confirmPayment(paymentIntentId);

      // Remove the payment_intent param from URL
      searchParams.delete("payment_intent");
      setSearchParams(searchParams);

      // Show success message
      toast.success(
        "🎉 " + t("products.subscriptionActivated")
      );

      // Refresh user data to get updated retailers
      await load();
    } catch (error: any) {
      console.error("Failed to confirm payment after redirect:", error);
      toast.error(
        error?.message || t("products.failedToProcessPayment")
      );

      // Remove the payment_intent param even on error
      searchParams.delete("payment_intent");
      setSearchParams(searchParams);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const settingsOptions = [
    {
      id: "profile",
      label: t("products.profileLabel"),
      icon: User,
      description: t("products.profileDescription"),
    },
    {
      id: "billing",
      label: t("settings.billing"),
      icon: CreditCard,
      description: t("products.billingDescription"),
    },
    {
      id: "notifications",
      label: t("settings.notifications"),
      icon: Bell,
      description: t("products.notificationsDescription"),
    },
	...(MEMBER_REFERRALS_ENABLED ? [{
		id: "referrals",
		label: "Invite friends",
		icon: Gift,
		description: "Give 50% off and bank up to 12 half-price renewals a year",
	}] : []),
	{
      id: "support",
      // "Help", as in the app: opens the contact screen (chat first, then email, then Discord).
      label: t("emptyStates.help.menuLabel"),
      icon: MessageCircle,
      description: t("products.supportDescription"),
    },
    {
      id: "language",
      label: t("settings.language"),
      icon: Languages,
      description: t("products.languageDescription"),
    },
  ];

  const renderTabContent = () => {
    const commonProps = {
      user,
      t,
      setActiveTab,
    };

    switch (activeTab) {
      case "profile":
        return <ProfileTab {...commonProps} />;
      case "billing":
        return <BillingTab {...commonProps} />;
      case "notifications":
        return <NotificationsTab {...commonProps} />;
		case "referrals":
			return MEMBER_REFERRALS_ENABLED ? <ReferralsTab /> : null;
      case "language":
        return (
          <LanguageTab
            {...commonProps}
            currentLanguage={currentLanguage}
            setLanguage={setLanguage}
          />
        );
      default:
        return null;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-dark-300">{t("settings.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-dark-950">
      {/* Payment Processing Overlay */}
      {isProcessingPayment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 max-w-md mx-4 text-center shadow-xl">
            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t("products.activatingSubscription")}
            </h3>
            <p className="text-gray-600 dark:text-dark-300">
              {t("products.activatingSubscriptionDesc")}
            </p>
          </div>
        </div>
      )}

      <div className="mx-auto flex-1 w-full max-w-4xl overflow-x-hidden overflow-y-auto px-4 py-4 pb-20 sm:px-6 lg:px-8 md:pb-4">
        {activeTab === null ? (
          /* Main Settings Options List */
          <div className="space-y-4">
            <div className="border-b border-gray-200 pb-5 dark:border-dark-700">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t("settings.title")}
              </h1>
              <p className="text-sm text-gray-700 dark:text-dark-100">
                {t("settings.description")}
              </p>
            </div>

            <div className="overflow-hidden border-b border-gray-200 dark:border-dark-700">
              <div className="divide-y divide-gray-200 dark:divide-dark-700">
                {settingsOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={() => {
                        if (option.id === "support") {
                          navigate("/contact-us");
                        } else {
                          setActiveTab(option.id as TabType);
                        }
                      }}
                      className="flex w-full items-center justify-between py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-dark-900/40"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-4 w-4 flex-shrink-0 text-gray-800 dark:text-dark-100" />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">
                            {option.label}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-dark-200">
                            {option.description}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-dark-300" />
                    </button>
                  );
                })}
                {isFreeTier(user) && <FreeAccountRows t={t} />}
              </div>
            </div>
          </div>
        ) : (
          /* Individual Setting View */
          <div className="space-y-4">
            <div className="border-b border-gray-200 pb-4 dark:border-dark-700">
              <button
                onClick={() => setActiveTab(null)}
                className="flex items-center gap-2 text-gray-800 transition-colors hover:text-gray-950 dark:text-dark-100 dark:hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">{t("products.backToSettings")}</span>
              </button>
            </div>
            <div className="pb-4">
              <div className="px-0 py-1 sm:px-0">{renderTabContent()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
